import { UserContext } from 'contexts/user';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { trackEvent } from 'services/analytics-service';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _iub: any;
  }
}

export const CookieBanner = () => {
  const { t, i18n } = useTranslation();
  const { inTeams } = useContext(UserContext);

  useEffect(() => {
    // do not show the cookie banner in the Teams app
    if (inTeams) {
      return;
    }

    // IMPORTANT: Do not replace this code with the auto-generated embed code from Iubenda.
    // You'll need to add/remove/update the options below based on the config changes made in Iubenda.
    // https://www.iubenda.com/en/help/1205-how-to-configure-your-cookie-solution-advanced-guide
    window._iub.csConfiguration = {
      askConsentAtCookiePolicyUpdate: true,
      cookiePolicyInOtherWindow: true,
      countryDetection: true,
      enableLgpd: true,
      enableUspr: true,
      gdprAppliesGlobally: false,
      lang: i18n.resolvedLanguage,
      lgpdAppliesGlobally: false,
      perPurposeConsent: true,
      purposes: '4,1,5',
      siteId: 3191065,
      showBannerForUS: true,
      usprApplies: true,
      usprPurposes: 'adv',
      whitelabel: false,
      cookiePolicyId: 62935495,
      floatingPreferencesButtonDisplay: false,
      cookiePolicyUrl: 'http://www.betterup.com/cookie-policy',
      privacyPolicyUrl: 'http://www.betterup.com/supp-privacy-notice-1',
      privacyPolicyNoticeAtCollectionUrl: 'http://www.betterup.com/supp-privacy-notice-1',
      banner: {
        acceptButtonColor: '#CE0058',
        acceptButtonDisplay: true,
        backgroundColor: '#2B333F',
        closeButtonRejects: true,
        customizeButtonColor: '#2B333F',
        customizeButtonDisplay: true,
        explicitWithdrawal: true,
        listPurposes: true,
        position: 'bottom',
        rejectButtonColor: '#CE0058',
        rejectButtonDisplay: true,
        showPurposesToggles: true,
      },
      callback: {
        onReady: () => {
          const gdprApplies = window._iub.cs.api.gdprApplies();
          const consentGiven = window._iub.cs.api.isConsentGiven();

          // we can't track analytics if GDPR applies and they haven't given consent
          if (gdprApplies && !consentGiven) {
            return;
          } else {
            // store the consent cookie for non-GDPR countries
            window._iub.cs.api.storeConsent();
            trackEvent({ eventName: 'Page viewed', eventProperties: { page_name: document.title } });
          }
        },
        onConsentGiven: () => {
          const gdprApplies = window._iub.cs.api.gdprApplies();
          if (gdprApplies) {
            trackEvent({ eventName: 'Page viewed', eventProperties: { page_name: document.title } });
          }
        },
      },
      i18n: {
        en: {
          banner: {
            title: t('cookieBanner.title'),
            body: t('cookieBanner.content'),
            dynamic: {
              body: `
                [if not gdprApplies]
                  ${t('cookieBanner.content')}
                [/if]
                [if gdprApplies]
                  ${t('cookieBanner.gdprContent')}
                [/if]
              `,
            },
            cookie_policy_caption: t('cookieBanner.cookiePolicyCaption'),
            privacy_policy_caption: t('cookieBanner.privacyNoticeCaption'),
          },
        },
        fr: {
          banner: {
            title: t('cookieBanner.title'),
            body: t('cookieBanner.content'),
            dynamic: {
              body: `
                [if not gdprApplies]
                  ${t('cookieBanner.content')}
                [/if]
                [if gdprApplies]
                  ${t('cookieBanner.gdprContent')}
                [/if]
              `,
            },
            cookie_policy_caption: t('cookieBanner.cookiePolicyCaption'),
            privacy_policy_caption: t('cookieBanner.privacyNoticeCaption'),
          },
        },
        de: {
          banner: {
            title: t('cookieBanner.title'),
            body: t('cookieBanner.content'),
            dynamic: {
              body: `
                [if not gdprApplies]
                  ${t('cookieBanner.content')}
                [/if]
                [if gdprApplies]
                  ${t('cookieBanner.gdprContent')}
                [/if]
              `,
            },
            cookie_policy_caption: t('cookieBanner.cookiePolicyCaption'),
            privacy_policy_caption: t('cookieBanner.privacyNoticeCaption'),
          },
        },
      },
    };

    const scriptIubendaStub = document.createElement('script');
    scriptIubendaStub.src = '//cdn.iubenda.com/cs/gpp/stub.js';
    document.body.appendChild(scriptIubendaStub);

    const scriptIubenda = document.createElement('script');
    scriptIubenda.src = '//cdn.iubenda.com/cs/iubenda_cs.js';
    scriptIubenda.async = true;
    document.body.appendChild(scriptIubenda);
  }, [i18n.resolvedLanguage, inTeams, t]);

  return null;
};
